import React, { useState, useEffect } from 'react';
import { Typography, Box, Paper, TextField, Container, Grid, Slider, withStyles, Button } from '@material-ui/core';
import '../../assets/styles/Step4/Step4.css';
import GenerateModal from './GenerateModal';
import {consoleDevLog} from "../../apiConfig";

const StyledSlider = withStyles({
  disabled: {
    "& .MuiSlider-track": {
      backgroundColor: "#3f51b5"
    },
    "& .MuiSlider-thumb": {
      backgroundColor: "#3f51b5"
    }
  }
})(Slider);

const Step4 = ({
    currentStep,
    finalSelectedHomes,
    realtorComments,
    setRealtorComments,
    priceRange,
    setPriceRange,
    adjustments,
    subjectProperty,
    cmaData,
    updateClientResources,
    history,
    generateModalOpen,
    onCloseGenerateModal,
}) => {

  const getStepClass = (stepIndex) => {
    return currentStep === stepIndex ? 'active-step' : 'inactive-step';
  };

  const selectedHomes = finalSelectedHomes;

  consoleDevLog(selectedHomes);
  consoleDevLog(adjustments);

  const [localRealtorComments, setLocalRealtorComments] = useState(realtorComments);
  // const [isModalOpen, setIsModalOpen] = useState(false);

  // const handleOpenModal = () => setIsModalOpen(true);
  // const handleCloseModal = () => setIsModalOpen(false);

  // Calculate the adjusted prices for each home
  const adjustedPrices = finalSelectedHomes.map(home => {
    const homeAdjustments = adjustments[home.Ml_num] || [];
    const adjustmentSum = homeAdjustments.reduce((adjustmentSum, adjustment) => {
      return adjustment.selected ? adjustmentSum + adjustment.amount : adjustmentSum;
    }, 0);
    return home.Sp_dol + adjustmentSum;
  });

  // Calculate the lower and upper range using the lowest and highest adjusted home price
  const lowerRange = Math.min(...adjustedPrices);
  const upperRange = Math.max(...adjustedPrices);
  const finalPrice = Math.round(adjustedPrices.reduce((sum, price) => sum + price, 0) / adjustedPrices.length);

  // Set default final price estimate as average price
  const [customFinalPrice, setCustomFinalPrice] = useState(finalPrice);

  // Update the priceRange in the parent component
  useEffect(() => {
    // Round to nearest integer
    const roundedLowerRange = Math.round(lowerRange);
    const roundedUpperRange = Math.round(upperRange);

    // Format with commas
    const formattedLowerRange = roundedLowerRange.toLocaleString();
    const formattedUpperRange = roundedUpperRange.toLocaleString();

    setPriceRange({
      lower: roundedLowerRange,
      estimated: finalPrice,
      upper: roundedUpperRange,
    });
  }, [lowerRange, upperRange, finalPrice, setPriceRange]);

  // Calculate the price range for the slider display.
  let sliderPriceRange = upperRange - lowerRange;
  let sliderMin = lowerRange - sliderPriceRange * 0.1; // Subtract 5% of the range from the lower range.
  let sliderMax = upperRange + sliderPriceRange * 0.1; // Add 5% of the range to the upper range.

  return (
    <div className={getStepClass(4)}>
      <Container maxWidth="lg">
        {/* Centered Final Estimated Price Section */}
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={8}>
            <Box className="final-price-box" padding={4} boxShadow={2} marginBottom={2}>
              <Typography variant="h5" align="center">Final Estimated Price: ${Math.round(finalPrice).toLocaleString()}</Typography>
              <StyledSlider
                style={{ width: '100%', margin: '0 auto' }}
                defaultValue={
                  selectedHomes.length > 1
                    ? [lowerRange, finalPrice, upperRange]
                    : finalPrice
                }
                min={selectedHomes.length > 1 ? sliderMin : finalPrice - 1000}  // Adjust as needed for centering
                max={selectedHomes.length > 1 ? sliderMax : finalPrice + 1000}  // Adjust as needed for centering
                step={0.01}
                disabled
                marks={[
                  ...(selectedHomes.length > 1
                    ? [
                        {
                          value: lowerRange,
                          label: (
                            <div style={{ textAlign: 'center' }}>
                              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                Low Estimate
                              </Typography>
                              <Typography variant="h6">${Math.round(lowerRange).toLocaleString()}</Typography>
                            </div>
                          ),
                        },
                      ]
                    : []),
                  {
                    value: finalPrice,
                    label: (
                      <div style={{ textAlign: 'center' }}>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          Estimated Price
                        </Typography>
                        <Typography variant="h6">${Math.round(finalPrice).toLocaleString()}</Typography>
                      </div>
                    ),
                  },
                  ...(selectedHomes.length > 1
                    ? [
                        {
                          value: upperRange,
                          label: (
                            <div style={{ textAlign: 'center' }}>
                              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                High Estimate
                              </Typography>
                              <Typography variant="h6">${Math.round(upperRange).toLocaleString()}</Typography>
                            </div>
                          ),
                        },
                      ]
                    : []),
                ]}
                valueLabelDisplay="auto"
                aria-labelledby="range-slider"
              />



            </Box>
          </Grid>
        </Grid>

        {/* New Custom Final Price Estimate Section */}
        <Box padding={2} boxShadow={2} marginBottom={2}>
          <Typography variant="h5">Final Price Estimate</Typography>
          <TextField
            label="Custom Final Price"
            variant="outlined"
            fullWidth
            value={customFinalPrice}
            onChange={(event) => setCustomFinalPrice(event.target.value)}
            style={{ marginTop: 10 }}
          />
        </Box>

        {/* Based on the following comparables */}
        <Typography variant="h6" className="comparable-title">Based on the following comparables:</Typography>
        <Grid container spacing={3} alignItems="stretch">
          {selectedHomes.map(home => {
            const selectedAdjustments = adjustments[home.Ml_num]
              ? adjustments[home.Ml_num].filter(adjustment => adjustment.selected)
              : [];

            const adjustmentSum = selectedAdjustments.reduce((sum, adjustment) => sum + adjustment.amount, 0);
            const adjustedPrice = home.Sp_dol + adjustmentSum;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={home.id}>
                <Paper elevation={2} style={{ padding: '20px', marginTop: '10px', marginBottom: '10px', height: '100%' }}>
                  <Typography variant="subtitle1">{home.Addr}</Typography>
                  <Typography variant="body2">Original Price: ${home.Sp_dol.toLocaleString()}</Typography>
                  <Typography variant="body2">Adjusted Price: ${adjustedPrice.toLocaleString()}</Typography>
                  <Typography variant="body2">
                    Adjustments: {selectedAdjustments.length > 0
                      ? selectedAdjustments.map(adjustment => adjustment.description).join(', ')
                      : 'No adjustments selected'}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Realtor's Comments Section with Title */}
        <Box padding={2} boxShadow={2} marginTop={2}>
          <Typography variant="h5">Realtor's Comments</Typography>
          <TextField
            label="Realtor's comments"
            multiline
            minRows={4}
            variant="outlined"
            value={localRealtorComments}
            onChange={(event) => {
              setLocalRealtorComments(event.target.value);
              setRealtorComments(event.target.value);
            }}
            style={{ marginTop: 10, width: '100%' }}
          />
        </Box>

        <GenerateModal
          open={generateModalOpen}
          onClose={onCloseGenerateModal}
          initialData={{
            cmaName: '', // Default or dynamic CMA name
            client: '', // Default or dynamic client
          }}
          adjustments={adjustments}
          selectedHomes={selectedHomes}
          finalSelectedHomes={finalSelectedHomes}
          priceRange={priceRange}
          realtorComments={realtorComments}
          subjectProperty={subjectProperty}
          cmaData={cmaData}
          updateClientResources={updateClientResources}
          history={history}
          shouldRedirect={true} 
        />

      </Container>
    </div>
  );
};

export default Step4;
